import cc from 'classcat';
import { useRef, useCallback, useEffect } from 'react';
import { FONT_SIZE_SMALL_IN_EM, FONT_SIZE_MEDIUM_IN_EM, FONT_SIZE_LARGE_IN_EM, FONT_SIZE_EXTRA_LARGE_IN_EM, BASE_FONT_SIZE } from '@i-ark/constants';
import { setAlphaOnColor } from '@i-ark/utils';
import { fontSizeContainerCls, scaleContainerCls, containerCls } from './Text.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useScale } from '../hooks/useScale.js';

const Text = ({
  sheetRef,
  pageId,
  pageAspectRatio,
  zIndex,
  item,
  isSelected,
  isCloned,
  onSelect,
  onDragStart,
  defaultTextColor,
  Text,
  Before,
  After
}) => {
  const scale = useScale();
  const ref = useRef(null);
  const handleSelect = useCallback(evt => {
    if (onSelect !== undefined) {
      onSelect(evt, item);
    }
  }, [item, onSelect]);
  useEffect(() => {
    const node = ref.current;
    if (node === null) {
      return;
    }
    node.addEventListener("focusin", handleSelect, {
      passive: false
    });
    node.addEventListener("mousedown", handleSelect, {
      passive: false
    });
    node.addEventListener("touchstart", handleSelect, {
      passive: false
    });
    return () => {
      node.removeEventListener("focusin", handleSelect);
      node.removeEventListener("mousedown", handleSelect);
      node.removeEventListener("touchstart", handleSelect);
    };
  }, [item, handleSelect]);
  let fontSize;
  switch (item.style.font.size) {
    case "extra-large":
      fontSize = `${FONT_SIZE_EXTRA_LARGE_IN_EM.toString()}em`;
      break;
    case "large":
      fontSize = `${FONT_SIZE_LARGE_IN_EM.toString()}em`;
      break;
    case "medium":
      fontSize = `${FONT_SIZE_MEDIUM_IN_EM.toString()}em`;
      break;
    case "small":
      fontSize = `${FONT_SIZE_SMALL_IN_EM.toString()}em`;
      break;
    default:
      fontSize = `${item.style.font.size.toString()}em`;
  }
  const hasBorder = item.style.border !== undefined && item.style.border.width > 0 && item.style.border.style !== "hidden" && item.style.border.color !== "transparent";
  const isTransparent = item.style.background.color === "transparent" || typeof item.style.background.opacity === "number" && !item.style.background.opacity;
  const handlePointerDragStart = evt => {
    if (!item.settings.cloneable && !item.settings.movable) {
      return;
    }
    onDragStart?.(evt, item);
  };
  const handleKeyDown = evt => {
    if ((evt.key === " " || evt.key === "Enter") && !document.body.classList.contains("isDragging")) {
      onDragStart?.(evt, item);
    }
  };
  return (
    /*#__PURE__*/
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    jsxs("div", {
      ref: ref,
      onMouseDown: handlePointerDragStart,
      onTouchStart: handlePointerDragStart,
      onKeyDown: handleKeyDown,
      "data-drag-target": true,
      "data-id": item.id,
      className: cc([containerCls, {
        isTransparent,
        bordered: hasBorder,
        withPadding: item.style.mockDefaultBrowserPadding,
        verticallyCenter: item.style.verticallyCenterContent,
        movable: item.settings.movable,
        cloneable: item.settings.cloneable
      }]),
      style: {
        zIndex,
        left: `${(item.position.x * 100).toString()}%`,
        top: `${(item.position.y * 100).toString()}%`,
        width: `${(item.size.width * 100).toString()}%`,
        height: `${(item.size.height * 100).toString()}%`,
        backgroundColor: item.style.background.color === "transparent" ? undefined : setAlphaOnColor(item.style.background.color, item.style.background.opacity ?? 1),
        border: item.style.border !== undefined ? `${Math.max(1, Math.round(item.style.border.width * BASE_FONT_SIZE * scale)).toString()}px ${item.style.border.style} ${item.style.border.color}` : undefined,
        borderRadius: item.style.border?.radius !== undefined ? `${item.style.border.radius.toString()}em` : undefined,
        fontFamily: item.style.font.family,
        color: item.style.font.color ?? defaultTextColor
      },
      children: [Before !== undefined && /*#__PURE__*/jsx(Before, {
        sheetRef: sheetRef,
        pageId: pageId,
        pageAspectRatio: pageAspectRatio,
        isSelected: isSelected,
        isCloned: isCloned,
        item: item
      }), /*#__PURE__*/jsx("div", {
        className: fontSizeContainerCls,
        children: /*#__PURE__*/jsx("div", {
          className: scaleContainerCls,
          style: {
            fontSize
          },
          children: /*#__PURE__*/jsx("span", {
            children: /*#__PURE__*/jsx(Text, {
              sheetRef: sheetRef,
              pageId: pageId,
              pageAspectRatio: pageAspectRatio,
              item: item,
              isSelected: isSelected,
              isCloned: isCloned
            })
          })
        })
      }), After !== undefined && /*#__PURE__*/jsx(After, {
        sheetRef: sheetRef,
        pageId: pageId,
        pageAspectRatio: pageAspectRatio,
        isSelected: isSelected,
        isCloned: isCloned,
        item: item,
        onDragStart: onDragStart
      })]
    })
  );
};

export { Text };
//# sourceMappingURL=Text.js.map
